// UTILS
import { ref } from "vue"
import { defineStore } from "pinia"
import { getDeviceType } from "@/helpers/getDeviceType"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { sleep } from "@/lib/utils"

// TYPES
import type { Answers, Answer } from "@/features/move/store/move.types"

// STORES
import {
  factoryPatch,
  factoryReset,
  initAnswers,
  initExtra,
} from "@/features/move/store/move.data"
import { useUserStore } from "@/stores/user"
import { useConfigStore } from "@/stores/config"
import { useQuestionsStore } from "@/features/move/store/questions.store"

// API
import {
  getAssessment,
  patchAssessment,
  postAssessment,
  deleteAssessment,
} from "@/api/viktor/assessment/assessment"

export const useAnswersStore = defineStore("moveAnswersStore", () => {
  const version = ref<string>("fr_FR_3.0.0")

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const { goNext, setCurrentIndex } = useQuestionsStore()
  const userStore = useUserStore()
  const configStore = useConfigStore()

  // GETTERS

  /**
   * Sync the answers with the backend
   */
  async function syncAssessment() {
    const assessment = await getAssessment("move")
    const currentAnswers = answers.value

    const newLength = assessment?.data?.items?.length ?? 0
    const currentLength = answers.value?.data?.items?.length ?? 0

    answers.value = assessment

    // We don't want to override the answers if the new length is less than the current length
    if (newLength < currentLength) {
      answers.value.data.items = currentAnswers.data.items
    }

    return assessment
  }

  /**
   * Fetch the move assessment from the API
   * @returns The move assessment
   */
  async function fetchMove() {
    const assessment = await syncAssessment()

    if (Object.keys(assessment).length === 0) {
      return assessment
    } else {
      // if no move assessmenent, redirect to stepper
      if (assessment?.is_active !== 1) {
        document.location.href = configStore.urls.app
      } else {
        // autrement on récupère les données du test et on popule les stores
        answers.value = assessment
        if (Object.keys(answers.value?.data || {}).length === 0) {
          setCurrentIndex(0)
          answers.value.data.extra = initAnswers.data.extra
        } else {
          setCurrentIndex(answers.value.data?.items?.length)
        }

        userStore.audio = answers.value.data?.extra?.general_audio
          ? true
          : false
        answers.value.data.extra = assessment.data.extra
      }
    }

    return answers.value
  }

  /**
   * Get the elapsed time of the assessment
   * @returns The elapsed time
   */
  function getElapsedTime() {
    let time = 0
    answers.value.data?.items?.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  // ACTIONS

  /**
   * Initialize the move assessment
   * @returns The created assessment
   */
  async function initMove() {
    const payload: Answers = {
      version: version.value,
      locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
      status: "todo",
      data: { items: [], extra: { ...initExtra } },
    }
    const data = await postAssessment("move", payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  /**
   * Submit an answer for a specific item
   * @param item_id The ID of the item
   * @param answer The answer to submit
   * @param time The time taken to answer
   */
  async function submitAnswer(item_id: number, answer: string, time: number) {
    if (
      !answers.value.data ||
      (Array.isArray(answers.value.data) && answers.value.data.length === 0)
    ) {
      answers.value.data = { items: [], extra: initExtra }
    }

    const alreadyAnswered = answers.value.data.items.find(
      (a) => a.item_id && a.item_id === item_id
    )
    if (!alreadyAnswered) {
      const item: Answer = {
        item_id: item_id,
        answer: answer,
        audio: userStore.audio,
        device_type: getDeviceType(),
        screen_width: screen.width,
        time_initial: time,
      }
      answers.value.data.items.push(item)

      answers.value.data.extra.general_audio = userStore.audio

      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }
      await patchAssessment("move", payload)
    }
    await goNext()
  }

  /**
   * Patch the extra data of the assessment
   * @param type The type of extra data to patch
   * @param answer The answer to patch
   */
  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer
      answers.value.data.extra.elapsed_time = getElapsedTime()

      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }
      await patchAssessment("move", payload)
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  /**
   * Submit the final results of the assessment
   * @returns True if the results were submitted successfully, false otherwise
   */
  async function submitFinalResults() {
    try {
      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true
      const payload: Answers = {
        version: version.value,
        locale_id: 1, // force to 1 = fr_FR and not dynamic locale as locale_id.value,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchAssessment("move", payload)

      MixpanelInstance.track("dmove_finish", {
        payload: payload,
      })

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  /**
   * Retake the test
   */
  async function retakeTest() {
    await deleteAssessment("move")
    await initMove()
    await userStore.fetchAssessmentSummary()
  }

  /**
   * Patch the assessment with factory data
   */
  async function patchFactoryTest() {
    await patchAssessment("move", factoryPatch)
    window.location.reload()
  }

  /**
   * Reset the user data with factory data
   */
  async function resetUserDataLight() {
    await patchAssessment("move", factoryReset)
    window.location.reload()
  }

  return {
    answers,
    fetchMove,
    initMove,
    submitAnswer,
    patchExtra,
    isPatchExtraLoading,
    submitFinalResults,
    retakeTest,
    patchFactoryTest,
    resetUserDataLight,
  }
})
